import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { PrimeReactProvider } from 'primereact/api';

import { ConfirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import './App.css';

import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import TopBar from './layout/TopBar';
import AppRoutes from './routes/AppRoutes';
import ChangePasswordDialog from './modals/ChangePasswordDialog';
import { useConfirmLogoutDialog } from './hooks/useConfirmLogoutDialog';

const getUserFromStorage = () => {
  try {
    const item = localStorage.getItem('user');
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error("Lỗi khi đọc dữ liệu user, tiến hành xóa dữ liệu lỗi:", error);
    localStorage.removeItem('user');
    return null;
  }
};

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useRef(null);
  const userMenuRef = useRef(null);

  const isLoginPage = location.pathname === '/login';

  const isPublicRoute = [
    '/public-dashboard',
    '/public-de-tai',
    '/public-sach-giao-trinh',
    '/public-nckh-khac',
    '/public-bai-bao',
    '/public-bai-bao-quoc-te',
    '/public-bai-bao-q1-q2',
    '/public-bai-bao-dong-tac-gia-quoc-te'
  ].includes(location.pathname);

  const [user, setUser] = useState(getUserFromStorage());
  const isAuthenticated = !!user;

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isPassModalOpen, setIsPassModalOpen] = useState(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);

  useEffect(() => {
    const handleStorageChange = () => setUser(getUserFromStorage());

    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setIsUserDropdownOpen(false);
    };

    const handleResize = () => {
      const mobileView = window.innerWidth <= 992;
      setIsMobile(mobileView);
      if (!mobileView) setIsSidebarCollapsed(false);
      else setIsSidebarCollapsed(true);
    };

    window.addEventListener('storage', handleStorageChange);
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);

    handleResize();

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const { confirmLogoutDialog } = useConfirmLogoutDialog();
  const handleLogout = () => confirmLogoutDialog({
    accept: () => { localStorage.removeItem('user'); window.location.href = '/login'; }
  });

  if (!isAuthenticated && !isLoginPage && !isPublicRoute) return <Navigate to="/login" replace />;
  if (isAuthenticated && isLoginPage) return <Navigate to="/" replace />;

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />

      {isLoginPage ? (
        <Routes><Route path="/login" element={<Login />} /></Routes>
      ) : isPublicRoute ? (
        <div style={{ width: '100%', minHeight: '100vh', backgroundColor: '#f4f7f6', overflowY: 'auto' }}>
          <AppRoutes />
        </div>
      ) : (
        <div className={`admin-container ${isSidebarCollapsed && !isMobile ? 'collapsed' : ''}`}>

          {isMobile && !isSidebarCollapsed && (
            <div className="mobile-backdrop" onClick={() => setIsSidebarCollapsed(true)}></div>
          )}

          <Sidebar
            isCollapsed={isSidebarCollapsed}
            setIsCollapsed={setIsSidebarCollapsed}
            isMobile={isMobile}
          />

          {isMobile && isSidebarCollapsed && (
            <button
              className="mobile-toggle-fab"
              onClick={() => setIsSidebarCollapsed(false)}
            >
              <i className="fa-solid fa-bars"></i>
            </button>
          )}

          <div className="main-content">
            <TopBar
              notifCount={notifCount}
              setNotifCount={setNotifCount}
              user={user}
              isUserDropdownOpen={isUserDropdownOpen}
              setIsUserDropdownOpen={setIsUserDropdownOpen}
              userMenuRef={userMenuRef}
              navigate={navigate}
              handleLogout={handleLogout}
              setIsPassModalOpen={setIsPassModalOpen}
            />
            <div className="page-wrapper">
              <AppRoutes triggerNotification={() => setNotifCount(prev => prev + 1)} />
            </div>
          </div>
        </div>
      )}

      {!isPublicRoute && (
        <ChangePasswordDialog
          isOpen={isPassModalOpen}
          onHide={() => setIsPassModalOpen(false)}
          user={user}
          toast={toast}
        />
      )}
    </>
  );
};

export default function App() {
  return (
    <PrimeReactProvider>
      <Router>
        <AppContent />
      </Router>
    </PrimeReactProvider>
  );
}